import { hasMatchingCode } from '@ctw/shared/api/fhir/codeable-concept';
import type { ObservationModel } from '@ctw/shared/api/fhir/models/observation';
import type { VitalsBucket } from '@ctw/shared/api/fhir/models/vitals-bucket';
import { SYSTEM_LOINC } from '@ctw/shared/api/fhir/system-urls';
import { Alert } from '@ctw/shared/components/alert';
import { Button } from '@ctw/shared/components/button';
import { Heading } from '@ctw/shared/components/heading';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { notify } from '@ctw/shared/components/toast';
import { useVitalWriteback } from '@ctw/shared/content/vitals/helpers/vital-write';
import { VitalsList } from '@ctw/shared/content/vitals/helpers/vitals-list';
import type { ModalState } from '@ctw/shared/context/modal-context';
import { useModal } from '@ctw/shared/context/modal-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useWritebacks } from '@ctw/shared/context/writeback-provider';
import { tw } from '@ctw/shared/utils/tailwind';
import { ValueSets } from '@ctw/shared/utils/value-sets';
import type { CodeableConcept } from 'fhir/r4';
import { useEffect, useMemo, useState } from 'react';
import { useSupportedVitals } from './use-supported-vitals';
import type { SupportedVitalsResponse } from './vital-write';

type SupportedVitals = SupportedVitalsResponse['data']['attributes'];

type VitalMapping = {
  bucketKey: keyof VitalsBucket;
  isSupported: (supportedVitals: SupportedVitals) => boolean;
  name: string;
  isCodeMatch: (code: CodeableConcept) => boolean;
};

const vitalMappings: Array<VitalMapping> = [
  {
    bucketKey: 'bloodPressure',
    isSupported: (supportedVitals) =>
      !!supportedVitals.find((v) => v.type === 'systolic') &&
      !!supportedVitals.find((v) => v.type === 'diastolic'),
    name: 'Blood Pressure',
    isCodeMatch: (code) =>
      hasMatchingCode(
        SYSTEM_LOINC,
        ValueSets.bpSystolic.content.map((c) => c.code),
        code,
      ) ||
      hasMatchingCode(
        SYSTEM_LOINC,
        ValueSets.bpDiastolic.content.map((c) => c.code),
        code,
      ),
  },
  {
    bucketKey: 'pulse',
    isSupported: (supportedVitals) => !!supportedVitals.find((v) => v.type === 'pulse'),
    name: 'Pulse',
    isCodeMatch: (code) =>
      hasMatchingCode(
        SYSTEM_LOINC,
        ValueSets.heartRate.content.map((c) => c.code),
        code,
      ),
  },
  {
    bucketKey: 'temperature',
    isSupported: (supportedVitals) => !!supportedVitals.find((v) => v.type === 'temperature'),
    name: 'Temperature',
    isCodeMatch: (code) =>
      hasMatchingCode(
        SYSTEM_LOINC,
        ValueSets.temperature.content.map((c) => c.code),
        code,
      ),
  },
  {
    bucketKey: 'respiratoryRate',
    isSupported: (supportedVitals) => !!supportedVitals.find((v) => v.type === 'respiratoryRate'),
    name: 'Respiratory Rate',
    isCodeMatch: (code) =>
      hasMatchingCode(
        SYSTEM_LOINC,
        ValueSets.respiratoryRate.content.map((c) => c.code),
        code,
      ),
  },
  {
    bucketKey: 'oxygenSaturation',
    isSupported: (supportedVitals) => !!supportedVitals.find((v) => v.type === 'o2Saturation'),
    name: 'Oxygen Saturation',
    isCodeMatch: (code) =>
      hasMatchingCode(
        SYSTEM_LOINC,
        ValueSets.o2.content.map((c) => c.code),
        code,
      ),
  },
  {
    bucketKey: 'height',
    isSupported: (supportedVitals) => !!supportedVitals.find((v) => v.type === 'height'),
    name: 'Height',
    isCodeMatch: (code) =>
      hasMatchingCode(
        SYSTEM_LOINC,
        ValueSets.height.content.map((c) => c.code),
        code,
      ),
  },
  {
    bucketKey: 'weight',
    isSupported: (supportedVitals) => !!supportedVitals.find((v) => v.type === 'weight'),
    name: 'Weight',
    isCodeMatch: (code) =>
      hasMatchingCode(
        SYSTEM_LOINC,
        ValueSets.weight.content.map((c) => c.code),
        code,
      ),
  },
  {
    bucketKey: 'bmi',
    isSupported: (supportedVitals) => !!supportedVitals.find((v) => v.type === 'bmi'),
    name: 'BMI',
    isCodeMatch: (code) =>
      hasMatchingCode(
        SYSTEM_LOINC,
        ValueSets.bmi.content.map((c) => c.code),
        code,
      ),
  },
  {
    bucketKey: 'headCircumference',
    isSupported: (supportedVitals) => !!supportedVitals.find((v) => v.type === 'headCircumference'),
    name: 'Head Circumference',
    isCodeMatch: (code) =>
      hasMatchingCode(
        SYSTEM_LOINC,
        ValueSets.headCircumference.content.map((c) => c.code),
        code,
      ),
  },
  {
    bucketKey: 'lmp',
    isSupported: (_) => false,
    name: 'LMP',
    isCodeMatch: (code) =>
      hasMatchingCode(
        SYSTEM_LOINC,
        ValueSets.lmp.content.map((c) => c.code),
        code,
      ),
  },
  {
    bucketKey: 'pain',
    isSupported: (supportedVitals) => !!supportedVitals.find((v) => v.type === 'pain'),
    name: 'Pain',
    isCodeMatch: (code) =>
      hasMatchingCode(
        SYSTEM_LOINC,
        ValueSets.pain.content.map((c) => c.code),
        code,
      ),
  },
];

interface VitalModalProps {
  vitalBucket: VitalsBucket;
  onEvent: ModalState['onModalEvent'];
  close: () => void;
}

const VitalModal = ({ vitalBucket, onEvent, close }: VitalModalProps) => {
  const [_error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const vitalWriteback = useVitalWriteback();
  const { onWritebackStart, onWritebackSuccess, onWritebackFailure } = useWritebacks();
  const { data: supportedVitals, isLoading } = useSupportedVitals();

  const unsupportedVitals = useMemo(() => {
    if (!supportedVitals) {
      return [];
    }
    return vitalMappings
      .filter(
        ({ bucketKey, isSupported: supportedCheck }) =>
          vitalBucket[bucketKey] && !supportedCheck(supportedVitals),
      )
      .map(({ name }) => name);
  }, [vitalBucket, supportedVitals]);

  const filteredBucket = useMemo(() => {
    if (!supportedVitals) {
      return vitalBucket;
    }
    const supportedVitalCodes = vitalMappings
      .filter(
        ({ bucketKey, isSupported: supportedCheck }) =>
          (bucketKey === 'bloodPressure' &&
            (supportedVitals.find((v) => v.type === 'systolic') ||
              supportedVitals.find((v) => v.type === 'diastolic'))) ||
          (bucketKey !== 'bloodPressure' && supportedCheck(supportedVitals)),
      )
      .map((m) => m.isCodeMatch);

    return vitalBucket.filter((vital: ObservationModel) => {
      const { code } = vital.resource;
      return supportedVitalCodes.some((check) => check(code));
    });
  }, [vitalBucket, supportedVitals]);

  useEffect(() => onEvent('open', () => setError(null)), [onEvent]);

  const handleAddToChart = async () => {
    setError(null);
    setIsSubmitting(true);

    onWritebackStart(filteredBucket.key);

    try {
      const response = await vitalWriteback(filteredBucket);

      onWritebackSuccess(filteredBucket.key, response);

      notify({
        type: 'success',
        title: 'Successfully added to chart',
        body: `Vitals taken on ${filteredBucket.dateDisplay} have been added to the patient's chart`,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';

      notify({
        type: 'error',
        title: 'Error adding vitals to chart',
        body: errorMessage,
      });

      onWritebackFailure(filteredBucket.key, errorMessage);
    } finally {
      setIsSubmitting(false);
      close();
    }
  };

  if (isLoading) {
    return (
      <div className={tw`flex items-center justify-center p-8`}>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className={tw`space-y-4`}>
      <Heading level="h1">Add to chart</Heading>
      {unsupportedVitals.length > 0 && (
        <Alert type="caution" header="Unsupported vitals">
          {`The following vitals will not be added to the patient's chart: ${unsupportedVitals.join(', ')}.`}
        </Alert>
      )}

      <p className={tw`text-gray-700`}>
        The following data will be imported to the chart for: Vitals taken on{' '}
        {filteredBucket.dateDisplay}
      </p>

      <VitalsList vitalBucket={filteredBucket} />

      <div className={tw`flex justify-end gap-3 pt-4`}>
        <Button type="button" variant="secondary" onClick={close} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={() => void handleAddToChart()}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className={tw`mr-2`}>Adding...</span>
              <LoadingSpinner />
            </>
          ) : (
            'Add to Chart'
          )}
        </Button>
      </div>
    </div>
  );
};

export function useVitalModal() {
  const telemetry = useTelemetry();
  const { openModal, onModalEvent, closeModal } = useModal({
    boundaryName: 'VitalModal',
  });

  return (vitalBucket: VitalsBucket) => {
    openModal({
      telemetry,
      action: {
        content: {
          body: <VitalModal onEvent={onModalEvent} close={closeModal} vitalBucket={vitalBucket} />,
        },
      },
    });
  };
}
